import { HTMLAttributes, useState } from 'react';
import { isAxiosError } from 'axios';
import { classes } from '../../../../lib/utils/cssUtils';
import { errorInfoIds } from '../../../../lib/tests/testIds';
import { ERROR_INFO_LABELS } from '../../../../lib/constants/labelKeys';
import { Label } from '../../../common/Label/Label';
import styles from './ErrorInfo.module.scss';
import Layout from '../../../common/Layout/Layout';
import { InternalComponent } from '../../../../lib/context/InternalComponentContext';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

export type ErrorInfoProps = {
  title?: string;
  description?: string;
  internal?: boolean;
  classNames?: Partial<Record<'topContainer', string>>;
  error?: unknown;
  errorAsTitle?: boolean;
} & HTMLAttributes<HTMLDivElement>;

const ErrorInfo = ({
  title,
  description,
  internal = false,
  className,
  classNames,
  error,
  errorAsTitle = false,
  ...rest
}: ErrorInfoProps) => {
  const [collapsed, setCollapsed] = useState(true);
  const { getParsedErrorMessage } = useErrorMapping();

  const handleClick = () => setCollapsed(prev => !prev);

  const parsedMessage = isAxiosError(error) ? getParsedErrorMessage(error) : description;
  const titleValue =
    errorAsTitle && parsedMessage ? parsedMessage : title ?? <Label name={ERROR_INFO_LABELS.ERROR_TITLE} />;

  return (
    <InternalComponent internal={internal}>
      <Layout className={classes(styles.container, className)} role="alert" {...rest}>
        <div className={classes(styles.topContainer, classNames?.topContainer)}>
          <p className={styles.title}>{titleValue}</p>
          {!!parsedMessage && !errorAsTitle && (
            <button data-testid={errorInfoIds.errorInfoCollapseButton} className={styles.button} onClick={handleClick}>
              <Label name={ERROR_INFO_LABELS.DETAILS_BUTTON} />
            </button>
          )}
        </div>
        {!!parsedMessage && !errorAsTitle && (
          <div
            className={classes(styles.collapseContainer, { [styles.open]: !collapsed })}
            data-testid={errorInfoIds.errorInfoCollapseContainer}
          >
            <p className={styles.description}>{parsedMessage}</p>
          </div>
        )}
      </Layout>
    </InternalComponent>
  );
};

export default ErrorInfo;
